import { type ReminderEntry, type ReminderSettings } from '../../../../../models/Scheduling'
import { type ReminderTemplateChoices } from '../../../../../store/ai/inputParametersHierarchy'
import { type ReminderSettingsDraft, settingsChanged } from '../reminderDrafts'

interface TemplateWriteBase {
  rowKey: string
  reminderEntryId: string
  templateId: string
}

export type TemplateWrite = (TemplateWriteBase & { kind: 'create' }) | (TemplateWriteBase & { kind: 'update' | 'delete'; nodeId: string })

export const hasTemplateChange = (draft: ReminderSettingsDraft): boolean => draft.rows.some((row) => row.templateId !== undefined)

export const savableDraft = (draft: ReminderSettingsDraft, settings: ReminderSettings): ReminderSettingsDraft =>
  draft.enabled
    ? draft
    : {
        ...draft,
        rows: draft.rows.flatMap((row) => {
          if (row.channels.length > 0) {
            return [row]
          }
          const saved = settings.entries.find((entry) => entry.id != null && entry.id === row.id)
          return saved != null ? [{ ...row, channels: saved.channels }] : []
        }),
      }

export const needsSettingsSave = (draft: ReminderSettingsDraft, settings: ReminderSettings): boolean =>
  settingsChanged(draft, settings) || draft.rows.some((row) => row.templateId !== undefined && row.id == null)

export const entryIdsByRowKey = (draft: ReminderSettingsDraft, savedEntries: ReminderEntry[]): Record<string, string | undefined> =>
  Object.fromEntries(draft.rows.map((row) => [row.key, row.id ?? savedEntries.find((entry) => entry.offsetMinutes === row.offsetMinutes)?.id ?? undefined]))

export const planTemplateWrites = (
  draft: ReminderSettingsDraft,
  idsByRowKey: Record<string, string | undefined>,
  choices: ReminderTemplateChoices,
  isZocksDefault: (templateId: string) => boolean,
): TemplateWrite[] =>
  draft.rows.flatMap((row): TemplateWrite[] => {
    const reminderEntryId = idsByRowKey[row.key]
    if (row.templateId === undefined || reminderEntryId == null) {
      return []
    }
    const node = choices[reminderEntryId]
    if (isZocksDefault(row.templateId)) {
      return node ? [{ rowKey: row.key, reminderEntryId, kind: 'delete', templateId: row.templateId, nodeId: node.id }] : []
    }
    return [
      node
        ? { rowKey: row.key, reminderEntryId, kind: 'update', templateId: row.templateId, nodeId: node.id }
        : { rowKey: row.key, reminderEntryId, kind: 'create', templateId: row.templateId },
    ]
  })

export const draftAfterPartialSave = (
  draft: ReminderSettingsDraft,
  idsByRowKey: Record<string, string | undefined>,
  failedRowKeys: string[],
): ReminderSettingsDraft => ({
  ...draft,
  rows: draft.rows.map((row) => ({
    ...row,
    id: idsByRowKey[row.key] ?? row.id,
    templateId: failedRowKeys.includes(row.key) ? row.templateId : undefined,
  })),
})
